import React from 'react';
import { styled } from '@compiled/react';
import { DatePicker } from '@atlaskit/datetime-picker';
import { ErrorMessage, Field } from '@atlaskit/form';
// eslint-disable-next-line import/order
import { token } from '@atlaskit/tokens';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { formatDateStringToISO } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { useLocale as useLocaleDI } from '@atlassian/jira-tenant-context-controller/src/components/locale/index.tsx';
import { LabelWrapper } from '../../../../common/ui/label/index.tsx';
import { DATE_PICKER_TYPES } from './constants';
import messages from './messages';
import type { OnUpdate } from './types';
import { dateRangeValidator, type AllowedDateRange } from './utils';

type Props = {
	loading: boolean;
	allowedDateRange: AllowedDateRange;
	selectedStart: {
		date?: string;
		invalidMessages: string[];
	};
	selectedEnd: {
		date?: string;
		invalidMessages: string[];
	};
	useLocale?: typeof useLocaleDI;
	onUpdate: OnUpdate;
};

const DatePickerGroup = ({
	loading,
	allowedDateRange,
	selectedStart,
	selectedEnd,
	onUpdate,
	useLocale = useLocaleDI,
}: Props) => {
	const locale = useLocale();
	const { formatMessage } = useIntl();
	const getMessages = (startDate?: string, endDate?: string) =>
		dateRangeValidator(
			allowedDateRange,
			{
				startDate,
				endDate,
			},
			formatMessage,
		);

	return (
		<GroupedDatePickerWrapper>
			<Spaced>
				<LabelWrapper data-test-id="servicedesk-reports-common.ui.date-filter.date-picker-group.connected-date-pickers-with-validator.from-label">
					<Field name="fromDate" label={formatMessage(messages.from)}>
						{() => (
							<DatePicker
								testId="servicedesk-reports-common.ui.date-filter.date-picker-group.connected-date-pickers-with-validator.data-picker-from"
								spacing="compact"
								value={formatDateStringToISO(selectedStart.date)}
								onChange={(date) => {
									onUpdate({
										type: DATE_PICKER_TYPES.START,
										date,
										invalidMessages: getMessages(date, selectedEnd.date),
									});
								}}
								innerProps={{ style: { width: gridSize * 20 } }}
								isDisabled={loading}
								locale={locale}
								isInvalid={Boolean(selectedStart.invalidMessages.length)}
							/>
						)}
					</Field>
				</LabelWrapper>
				{selectedStart.invalidMessages.map((message) => (
					<ErrorMessage key={message}>{message}</ErrorMessage>
				))}
			</Spaced>
			<Spaced>
				<LabelWrapper data-test-id="servicedesk-reports-common.ui.date-filter.date-picker-group.connected-date-pickers-with-validator.to-label">
					<Field name="toDate" label={formatMessage(messages.to)}>
						{() => (
							<DatePicker
								testId="servicedesk-reports-common.ui.date-filter.date-picker-group.connected-date-pickers-with-validator.date-picker-to"
								spacing="compact"
								value={formatDateStringToISO(selectedEnd.date)}
								onChange={(date) => {
									onUpdate({
										type: DATE_PICKER_TYPES.END,
										date,
										invalidMessages: getMessages(selectedStart.date, date),
									});
								}}
								innerProps={{ style: { width: gridSize * 20 } }}
								isDisabled={loading}
								locale={locale}
								isInvalid={Boolean(selectedEnd.invalidMessages.length)}
							/>
						)}
					</Field>
				</LabelWrapper>
				{selectedEnd.invalidMessages.map((message) => (
					<ErrorMessage key={message}>{message}</ErrorMessage>
				))}
			</Spaced>
		</GroupedDatePickerWrapper>
	);
};

export default DatePickerGroup;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupedDatePickerWrapper = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spaced = styled.div({
	marginLeft: token('space.300', '24px'),
});
